import React, { ReactElement, useState, useMemo } from 'react';
import {
  useAddManufacturer,
  useAddFrame,
  useAddTyre,
  useAddWheelFront,
  useAddWheelRear,
  useUpdateManufacturer,
  useUpdateFrame,
  useUpdateTyre,
  useUpdateWheelFront,
  useUpdateWheelRear,
  useUpdateEquipmentTeamParameter,
  useDeleteManufacturer,
  useDeleteFrame,
  useDeleteTyre,
  useDeleteWheelFront,
  useDeleteWheelRear,
  useEquipmentCatalog,
  useManufacturers,
  useDeleteEquipmentTeamParameter,
} from '../../hooks/useEquipment';
import { useUser } from 'hooks/useUser';

interface Manufacturer {
  id: string;
  name: string;
  notes?: string;
}

interface EquipmentFormProps {
  mode: 'add' | 'edit';
  equipmentType: string;
  manufacturers: Manufacturer[];
  equipment?: any; // Required for edit mode
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  manufacturer_id: string;
  designation: string;
  part_number: string;
  notes: string;
  keep_private: boolean;
  // Common field for frames, tyres, and wheels
  mass_g: string;
  // Tyre specific
  diameter_mm: string;
  width_mm: string;
  // Wheel specific
  width_inner_mm: string;
  width_outer_mm: string;
  depth_mm: string;
  size_nominal: string;
}

const EquipmentForm = ({
  mode,
  equipmentType,
  manufacturers,
  equipment,
  onSuccess,
  onCancel,
}: EquipmentFormProps): ReactElement => {
  const isEditMode = mode === 'edit';
  const [userProfile] = useUser();
  const isSuperuser = userProfile?.superuser === true;
  const [selectedCopyFrom, setSelectedCopyFrom] = useState<string>('');
  const [wheelCopyType, setWheelCopyType] = useState<'front' | 'rear'>(() => {
    // Default to the current wheel type
    return equipmentType === 'rearWheels' ? 'rear' : 'front';
  });

  const [formData, setFormData] = useState<FormData>(() => {
    if (isEditMode && equipment) {
      return {
        name: equipment.name || '',
        manufacturer_id: equipment.manufacturer_id || '',
        designation: equipment.designation || '',
        part_number: equipment.part_number || '',
        notes: equipment.notes || '',
        keep_private: equipment.team_parameters?.[0]?.keep_private ?? false,
        mass_g: equipment.mass_g?.toString() || '',
        diameter_mm: equipment.diameter_mm?.toString() || '',
        width_mm: equipment.width_mm?.toString() || '',
        width_inner_mm: equipment.width_inner_mm?.toString() || '',
        width_outer_mm: equipment.width_outer_mm?.toString() || '',
        depth_mm: equipment.depth_mm?.toString() || '',
        size_nominal: equipment.size_nominal || '',
      };
    }

    // Default values for add mode
    return {
      name: '',
      manufacturer_id: '',
      designation: '',
      part_number: '',
      notes: '',
      keep_private: false,
      mass_g: '',
      diameter_mm: '',
      width_mm: '',
      width_inner_mm: '',
      width_outer_mm: '',
      depth_mm: '',
      size_nominal: '',
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteError, setDeleteError] = useState<string>('');
  const [makePublic, setMakePublic] = useState<boolean>(false);

  // Get equipment catalog for copy functionality
  const { data: equipmentCatalog } = useEquipmentCatalog();
  const { data: manufacturersData } = useManufacturers();

  // Get available equipment to copy from based on type
  const availableEquipmentToCopy = useMemo(() => {
    if (!equipmentCatalog) return [];

    switch (equipmentType) {
      case 'manufacturers':
        return manufacturersData || [];
      case 'frames':
        return equipmentCatalog.frames || [];
      case 'tyres':
        return equipmentCatalog.tyres || [];
      case 'frontWheels':
      case 'rearWheels':
        // For wheels, allow copying from either front or rear based on radio selection
        return wheelCopyType === 'front' ? equipmentCatalog.wheels_front || [] : equipmentCatalog.wheels_rear || [];
      default:
        return [];
    }
  }, [equipmentCatalog, manufacturersData, equipmentType, wheelCopyType]);

  // Handle copy from selection
  const handleCopyFrom = (equipmentId: string) => {
    const selectedEquipment = availableEquipmentToCopy.find((eq: any) => eq.id === equipmentId);
    if (!selectedEquipment) return;

    // Copy all relevant fields from the selected equipment
    setFormData({
      name: selectedEquipment.name || '',
      manufacturer_id: selectedEquipment.manufacturer_id || '',
      designation: selectedEquipment.designation || '',
      part_number: selectedEquipment.part_number || '',
      notes: selectedEquipment.notes || '',
      keep_private: false, // Default to false for new items
      mass_g: selectedEquipment.mass_g?.toString() || '',
      diameter_mm: selectedEquipment.diameter_mm?.toString() || '',
      width_mm: selectedEquipment.width_mm?.toString() || '',
      width_inner_mm: selectedEquipment.width_inner_mm?.toString() || '',
      width_outer_mm: selectedEquipment.width_outer_mm?.toString() || '',
      depth_mm: selectedEquipment.depth_mm?.toString() || '',
      size_nominal: selectedEquipment.size_nominal || '',
    });

    // Clear the selection after copying
    setSelectedCopyFrom('');
  };

  // Add mutation hooks
  const addManufacturerMutation = useAddManufacturer();
  const addFrameMutation = useAddFrame();
  const addTyreMutation = useAddTyre();
  const addWheelFrontMutation = useAddWheelFront();
  const addWheelRearMutation = useAddWheelRear();

  // Update mutation hooks
  const updateManufacturerMutation = useUpdateManufacturer();
  const updateFrameMutation = useUpdateFrame();
  const updateTyreMutation = useUpdateTyre();
  const updateWheelFrontMutation = useUpdateWheelFront();
  const updateWheelRearMutation = useUpdateWheelRear();
  const updateTeamParameterMutation = useUpdateEquipmentTeamParameter();
  const deleteTeamParameterMutation = useDeleteEquipmentTeamParameter();

  // Delete mutation hooks
  const deleteManufacturerMutation = useDeleteManufacturer();
  const deleteFrameMutation = useDeleteFrame();
  const deleteTyreMutation = useDeleteTyre();
  const deleteWheelFrontMutation = useDeleteWheelFront();
  const deleteWheelRearMutation = useDeleteWheelRear();

  const isSubmitting =
    addManufacturerMutation.isPending ||
    addFrameMutation.isPending ||
    addTyreMutation.isPending ||
    addWheelFrontMutation.isPending ||
    addWheelRearMutation.isPending ||
    updateManufacturerMutation.isPending ||
    updateFrameMutation.isPending ||
    updateTyreMutation.isPending ||
    updateWheelFrontMutation.isPending ||
    updateWheelRearMutation.isPending ||
    deleteTeamParameterMutation.isPending;

  const isDeleting =
    deleteManufacturerMutation.isPending ||
    deleteFrameMutation.isPending ||
    deleteTyreMutation.isPending ||
    deleteWheelFrontMutation.isPending ||
    deleteWheelRearMutation.isPending;

  const getFormTitle = () => {
    const action = isEditMode ? 'Edit' : 'Add New';
    switch (equipmentType) {
      case 'manufacturers':
        return `${action} Manufacturer`;
      case 'frames':
        return `${action} Frame`;
      case 'tyres':
        return `${action} Tyre`;
      case 'frontWheels':
        return `${action} Front Wheel`;
      case 'rearWheels':
        return `${action} Rear Wheel`;
      default:
        return `${action} Equipment`;
    }
  };

  const getFormStyles = () => {
    if (isEditMode) {
      return {
        container: 'cr-bg-blue-50 cr-border cr-border-blue-200 cr-rounded-lg cr-p-4 cr-mt-4',
      };
    }
    return {
      container: 'cr-bg-gray-50 cr-border cr-border-gray-200 cr-rounded-lg cr-p-4 cr-mt-4',
    };
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name is always required
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Manufacturer is required for all except manufacturers table
    if (equipmentType !== 'manufacturers' && !formData.manufacturer_id) {
      newErrors.manufacturer_id = 'Manufacturer is required';
    }

    // Type-specific required fields
    if (equipmentType === 'tyres') {
      if (!formData.diameter_mm) {
        newErrors.diameter_mm = 'Diameter is required';
      }
      if (!formData.width_mm) {
        newErrors.width_mm = 'Width is required';
      }
    }

    if (equipmentType === 'frontWheels' || equipmentType === 'rearWheels') {
      if (!formData.diameter_mm) {
        newErrors.diameter_mm = 'Diameter is required';
      }
      if (!formData.depth_mm) {
        newErrors.depth_mm = 'Depth is required';
      }
      if (!formData.width_inner_mm) {
        newErrors.width_inner_mm = 'Inner width is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        await handleUpdate();
      } else {
        await handleAdd();
      }
      onSuccess();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'add'} equipment:`, error);
      setErrors((prev) => ({
        ...prev,
        _form: `Failed to ${isEditMode ? 'update' : 'add'} equipment. Please try again.`,
      }));
    }
  };

  // Helper function to convert empty strings to null for nullable fields
  const toNullIfEmpty = (value: string): string | null => {
    return value.trim() === '' ? null : value;
  };

  const toFloatOrNull = (value: string): number | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : parseFloat(trimmed);
  };

  const handleAdd = async () => {
    let result;

    switch (equipmentType) {
      case 'manufacturers':
        result = await addManufacturerMutation.mutateAsync({
          name: formData.name,
          notes: toNullIfEmpty(formData.notes),
          keep_private: formData.keep_private,
        });
        break;

      case 'frames':
        result = await addFrameMutation.mutateAsync({
          name: formData.name,
          manufacturer_id: formData.manufacturer_id,
          designation: toNullIfEmpty(formData.designation),
          part_number: toNullIfEmpty(formData.part_number),
          size_nominal: toNullIfEmpty(formData.size_nominal),
          mass_g: toFloatOrNull(formData.mass_g),
          notes: toNullIfEmpty(formData.notes),
          keep_private: formData.keep_private,
        });
        break;

      case 'tyres':
        result = await addTyreMutation.mutateAsync({
          name: formData.name,
          manufacturer_id: formData.manufacturer_id,
          designation: toNullIfEmpty(formData.designation),
          part_number: toNullIfEmpty(formData.part_number),
          diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
          width_mm: toFloatOrNull(formData.width_mm),
          mass_g: toFloatOrNull(formData.mass_g),
          notes: toNullIfEmpty(formData.notes),
          keep_private: formData.keep_private,
        });
        break;

      case 'frontWheels':
        result = await addWheelFrontMutation.mutateAsync({
          name: formData.name,
          manufacturer_id: formData.manufacturer_id,
          designation: toNullIfEmpty(formData.designation),
          part_number: toNullIfEmpty(formData.part_number),
          diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
          depth_mm: toFloatOrNull(formData.depth_mm),
          width_inner_mm: toFloatOrNull(formData.width_inner_mm),
          width_outer_mm: toFloatOrNull(formData.width_outer_mm),
          mass_g: toFloatOrNull(formData.mass_g),
          notes: toNullIfEmpty(formData.notes),
          keep_private: formData.keep_private,
        });
        break;

      case 'rearWheels':
        result = await addWheelRearMutation.mutateAsync({
          name: formData.name,
          manufacturer_id: formData.manufacturer_id,
          designation: toNullIfEmpty(formData.designation),
          part_number: toNullIfEmpty(formData.part_number),
          diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
          depth_mm: toFloatOrNull(formData.depth_mm),
          width_inner_mm: toFloatOrNull(formData.width_inner_mm),
          width_outer_mm: toFloatOrNull(formData.width_outer_mm),
          mass_g: toFloatOrNull(formData.mass_g),
          notes: toNullIfEmpty(formData.notes),
          keep_private: formData.keep_private,
        });
        break;

      default:
        throw new Error(`Unknown equipment type: ${equipmentType}`);
    }

    return result;
  };

  const handleUpdate = async () => {
    const id = equipment.id;

    switch (equipmentType) {
      case 'manufacturers':
        await updateManufacturerMutation.mutateAsync({
          id,
          data: {
            name: formData.name,
            notes: toNullIfEmpty(formData.notes),
          },
        });
        break;

      case 'frames':
        await updateFrameMutation.mutateAsync({
          id,
          data: {
            name: formData.name,
            manufacturer_id: formData.manufacturer_id,
            designation: toNullIfEmpty(formData.designation),
            part_number: toNullIfEmpty(formData.part_number),
            size_nominal: toNullIfEmpty(formData.size_nominal),
            mass_g: toFloatOrNull(formData.mass_g),
            notes: toNullIfEmpty(formData.notes),
          },
        });
        break;

      case 'tyres':
        await updateTyreMutation.mutateAsync({
          id,
          data: {
            id,
            name: formData.name,
            manufacturer_id: formData.manufacturer_id,
            designation: toNullIfEmpty(formData.designation),
            part_number: toNullIfEmpty(formData.part_number),
            diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
            width_mm: toFloatOrNull(formData.width_mm),
            mass_g: toFloatOrNull(formData.mass_g),
            notes: toNullIfEmpty(formData.notes),
          },
        });
        break;

      case 'frontWheels':
        await updateWheelFrontMutation.mutateAsync({
          id,
          data: {
            id,
            name: formData.name,
            manufacturer_id: formData.manufacturer_id,
            designation: toNullIfEmpty(formData.designation),
            part_number: toNullIfEmpty(formData.part_number),
            diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
            depth_mm: toFloatOrNull(formData.depth_mm),
            width_inner_mm: toFloatOrNull(formData.width_inner_mm),
            width_outer_mm: toFloatOrNull(formData.width_outer_mm),
            mass_g: toFloatOrNull(formData.mass_g),
            notes: toNullIfEmpty(formData.notes),
          },
        });
        break;

      case 'rearWheels':
        await updateWheelRearMutation.mutateAsync({
          id,
          data: {
            id,
            name: formData.name,
            manufacturer_id: formData.manufacturer_id,
            designation: toNullIfEmpty(formData.designation),
            part_number: toNullIfEmpty(formData.part_number),
            diameter_mm: parseFloat(formData.diameter_mm) || 700, // Has default value in DB
            depth_mm: toFloatOrNull(formData.depth_mm),
            width_inner_mm: toFloatOrNull(formData.width_inner_mm),
            width_outer_mm: toFloatOrNull(formData.width_outer_mm),
            mass_g: toFloatOrNull(formData.mass_g),
            notes: toNullIfEmpty(formData.notes),
          },
        });
        break;

      default:
        throw new Error(`Unknown equipment type: ${equipmentType}`);
    }

    // Handle team parameters
    // For superusers editing public equipment, skip team parameter updates
    const isPublicEquipment =
      !equipment.private && (!equipment.team_parameters || equipment.team_parameters.length === 0);
    const shouldSkipTeamParams = isPublicEquipment && isSuperuser;

    if (!shouldSkipTeamParams && equipment.team_parameters && equipment.team_parameters.length > 0) {
      const teamParamId = equipment.team_parameters[0].id;
      const currentKeepPrivate = equipment.team_parameters[0].keep_private;

      // If superuser wants to make it public, delete the team parameter
      if (isSuperuser && makePublic) {
        await deleteTeamParameterMutation.mutateAsync(teamParamId);
      } else if (currentKeepPrivate !== formData.keep_private) {
        // Only update if the keep_private value has changed
        await updateTeamParameterMutation.mutateAsync({
          id: teamParamId,
          data: {
            keep_private: formData.keep_private,
          },
        });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this equipment? This action cannot be undone.')) {
      return;
    }

    setDeleteError('');

    try {
      const id = equipment.id;

      switch (equipmentType) {
        case 'manufacturers':
          await deleteManufacturerMutation.mutateAsync(id);
          break;
        case 'frames':
          await deleteFrameMutation.mutateAsync(id);
          break;
        case 'tyres':
          await deleteTyreMutation.mutateAsync(id);
          break;
        case 'frontWheels':
          await deleteWheelFrontMutation.mutateAsync(id);
          break;
        case 'rearWheels':
          await deleteWheelRearMutation.mutateAsync(id);
          break;
        default:
          throw new Error(`Unknown equipment type: ${equipmentType}`);
      }

      onSuccess();
    } catch (error: any) {
      console.error('Failed to delete equipment:', error);

      // Check if it's a foreign key constraint error
      const errorMessage = error?.response?.data?.message || error?.message || '';
      if (
        errorMessage.includes('foreign key') ||
        errorMessage.includes('constraint') ||
        errorMessage.includes('violates') ||
        errorMessage.includes('referenced')
      ) {
        setDeleteError(
          'Cannot delete this equipment because it is being used in other records. Please remove all references before deleting.',
        );
      } else {
        setDeleteError('Failed to delete equipment. Please try again.');
      }
    }
  };

  const renderField = (field: keyof FormData, label: string, type: string = 'text', required: boolean = false) => (
    <div className="cr-flex cr-flex-col">
      <label className="cr-mb-1 cr-block cr-text-sm cr-font-medium cr-text-gray-700">
        {label}
        {required && <span className="cr-ml-1 cr-text-red-500">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          value={typeof formData[field] === 'boolean' ? String(formData[field]) : formData[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`cr-focus:outline-none cr-focus:ring-2 cr-focus:ring-blue-500
           cr-focus:border-blue-500 cr-w-full cr-resize-none 
            cr-rounded-md cr-border cr-px-3 cr-py-2 
            cr-text-sm cr-shadow-sm ${errors[field] ? 'cr-border-red-500' : 'cr-border-gray-300'}`}
          rows={2}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      ) : (
        <input
          type={type}
          value={typeof formData[field] === 'boolean' ? String(formData[field]) : formData[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`cr-focus:outline-none cr-focus:ring-2 cr-focus:ring-blue-500
           cr-focus:border-blue-500 cr-w-full cr-rounded-md 
            cr-border cr-px-3 cr-py-2 cr-text-sm 
            cr-shadow-sm ${errors[field] ? 'cr-border-red-500' : 'cr-border-gray-300'}`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      )}
      {errors[field] && <p className="cr-mt-1 cr-text-xs cr-text-red-600">{errors[field]}</p>}
    </div>
  );

  const renderManufacturerDropdown = () => (
    <div className="cr-flex cr-flex-col">
      <label className="cr-mb-1 cr-block cr-text-sm cr-font-medium cr-text-gray-700">
        Manufacturer
        <span className="cr-ml-1 cr-text-red-500">*</span>
      </label>
      <select
        value={formData.manufacturer_id}
        onChange={(e) => handleInputChange('manufacturer_id', e.target.value)}
        className={`cr-focus:outline-none cr-focus:ring-2 cr-focus:ring-blue-500
         cr-focus:border-blue-500 cr-w-full cr-rounded-md cr-border
         cr-px-3 cr-py-2 cr-text-sm cr-shadow-sm ${
           errors.manufacturer_id ? 'cr-border-red-500' : 'cr-border-gray-300'
         }`}
      >
        <option value="">Select a manufacturer</option>
        {manufacturers.map((manufacturer) => (
          <option key={manufacturer.id} value={manufacturer.id}>
            {manufacturer.name}
          </option>
        ))}
      </select>
      {errors.manufacturer_id && <p className="cr-mt-1 cr-text-xs cr-text-red-600">{errors.manufacturer_id}</p>}
    </div>
  );

  const shouldShowKeepPrivateCheckbox = () => {
    if (!isEditMode) {
      // Always show for add mode
      return true;
    }

    // For superusers editing public equipment, don't show the checkbox
    const isPublicEquipment =
      !equipment?.private && (!equipment?.team_parameters || equipment.team_parameters.length === 0);
    if (isPublicEquipment && isSuperuser) {
      return false;
    }

    // Only show for edit mode if equipment is private
    return equipment?.private;
  };

  const shouldShowMakePublicCheckbox = () => {
    if (!isEditMode || !isSuperuser) {
      return false;
    }

    // Only show if equipment is private and keep_private is not true
    const isPrivateEquipment =
      equipment?.private || (equipment?.team_parameters && equipment.team_parameters.length > 0);
    const keepPrivateValue = equipment?.team_parameters?.[0]?.keep_private;

    return isPrivateEquipment && keepPrivateValue !== true;
  };

  const styles = getFormStyles();

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between">
        <h3 className="cr-text-lg cr-font-medium cr-text-gray-900">{getFormTitle()}</h3>
        <button
          type="button"
          onClick={onCancel}
          className="cr-text-gray-400 cr-transition-colors hover:cr-text-gray-600"
        >
          <svg className="cr-h-5 cr-w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Copy from dropdown - only show in add mode and when there are items to copy */}
      {!isEditMode && equipmentType !== 'manufacturers' && (
        <div className="cr-mb-4 cr-rounded-md cr-border cr-border-blue-200 cr-bg-blue-50 cr-p-3">
          {/* Radio buttons for wheel type selection */}
          {(equipmentType === 'frontWheels' || equipmentType === 'rearWheels') && (
            <div className="cr-mb-3 cr-flex cr-items-center cr-gap-4">
              <span className="cr-text-sm cr-font-medium cr-text-gray-700">Copy from:</span>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1.5">
                <input
                  type="radio"
                  name="wheelType"
                  value="front"
                  checked={wheelCopyType === 'front'}
                  onChange={() => {
                    setWheelCopyType('front');
                    setSelectedCopyFrom(''); // Reset selection when switching
                  }}
                  className="cr-focus:ring-blue-500 cr-h-4 cr-w-4 cr-border-gray-300 cr-text-blue-600"
                />
                <span className="cr-text-sm cr-text-gray-700">Front Wheels</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-1.5">
                <input
                  type="radio"
                  name="wheelType"
                  value="rear"
                  checked={wheelCopyType === 'rear'}
                  onChange={() => {
                    setWheelCopyType('rear');
                    setSelectedCopyFrom(''); // Reset selection when switching
                  }}
                  className="cr-focus:ring-blue-500 cr-h-4 cr-w-4 cr-border-gray-300 cr-text-blue-600"
                />
                <span className="cr-text-sm cr-text-gray-700">Rear Wheels</span>
              </label>
            </div>
          )}

          {/* Copy from dropdown */}
          {availableEquipmentToCopy.length > 0 && (
            <div className="cr-flex cr-items-center cr-gap-3">
              <label className="cr-text-sm cr-font-medium cr-text-gray-700">Copy from existing:</label>
              <select
                value={selectedCopyFrom}
                onChange={(e) => {
                  setSelectedCopyFrom(e.target.value);
                  if (e.target.value) {
                    handleCopyFrom(e.target.value);
                  }
                }}
                className="cr-focus:outline-none cr-focus:ring-2 cr-focus:ring-blue-500 cr-focus:border-blue-500 cr-flex-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-1.5 cr-text-sm cr-shadow-sm"
              >
                <option value="">Select an item to copy...</option>
                {availableEquipmentToCopy.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.manufacturer && equipmentType !== 'manufacturers'
                      ? `${item.manufacturer.name} - ${item.name}`
                      : item.name}
                    {item.part_number && ` (${item.part_number})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show message if no items available */}
          {availableEquipmentToCopy.length === 0 && (
            <div className="cr-text-sm cr-text-gray-600">
              No {wheelCopyType === 'front' ? 'front wheels' : wheelCopyType === 'rear' ? 'rear wheels' : 'items'}{' '}
              available to copy from.
            </div>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Form-level errors */}
        {errors._form && (
          <div className="cr-mb-4 cr-rounded-md cr-border cr-border-red-200 cr-bg-red-50 cr-p-3">
            <p className="cr-text-sm cr-text-red-600">{errors._form}</p>
          </div>
        )}

        {/* Delete errors - only show in edit mode */}
        {isEditMode && deleteError && (
          <div className="cr-mb-4 cr-rounded-md cr-border cr-border-red-200 cr-bg-red-50 cr-p-3">
            <p className="cr-text-sm cr-text-red-600">{deleteError}</p>
          </div>
        )}

        {/* Form fields in responsive grid */}
        <div className="cr-mb-4 cr-grid cr-grid-cols-1 cr-gap-4 md:cr-grid-cols-2 lg:cr-grid-cols-3">
          {/* Name - Always required */}
          {renderField('name', 'Name', 'text', true)}

          {/* Manufacturer dropdown - not for manufacturer table */}
          {equipmentType !== 'manufacturers' && renderManufacturerDropdown()}

          {/* Common optional fields */}
          {equipmentType !== 'manufacturers' && (
            <>
              {renderField('designation', 'Designation')}
              {renderField('part_number', 'Part Number')}
            </>
          )}

          {/* Tyre specific fields */}
          {equipmentType === 'tyres' && (
            <>
              {renderField('diameter_mm', 'Diameter (mm)', 'number', true)}
              {renderField('width_mm', 'Width (mm)', 'number', true)}
              {renderField('mass_g', 'Mass (grams)', 'number')}
            </>
          )}

          {/* Wheel specific fields */}
          {(equipmentType === 'frontWheels' || equipmentType === 'rearWheels') && (
            <>
              {renderField('diameter_mm', 'Diameter (mm)', 'number', true)}
              {renderField('depth_mm', 'Depth (mm)', 'number', true)}
              {renderField('width_inner_mm', 'Inner Width (mm)', 'number', true)}
              {renderField('width_outer_mm', 'Outer Width (mm)', 'number')}
              {renderField('mass_g', 'Mass (grams)', 'number')}
            </>
          )}

          {/* Frame specific fields */}
          {equipmentType === 'frames' && (
            <>
              {renderField('size_nominal', 'Size')}
              {renderField('mass_g', 'Mass (grams)', 'number')}
            </>
          )}
        </div>

        {/* Notes field - full width */}
        <div className="cr-mb-4">{renderField('notes', 'Notes', 'textarea')}</div>

        {/* Keep Private checkbox with info icon */}
        {shouldShowKeepPrivateCheckbox() && (
          <div className="cr-mb-4">
            <div className="cr-flex cr-items-center cr-gap-2">
              <label className="cr-flex cr-items-center cr-gap-2">
                <input
                  type="checkbox"
                  checked={formData.keep_private}
                  onChange={(e) => handleInputChange('keep_private', e.target.checked)}
                  className="cr-focus:ring-blue-500 cr-focus:ring-2 cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-bg-gray-100 cr-text-blue-600"
                />
                <span className="cr-text-sm cr-font-medium cr-text-gray-700">Keep Private</span>
              </label>
              <div className="cr-group cr-relative">
                <svg
                  className="cr-h-4 cr-w-4 cr-cursor-help cr-text-gray-400 hover:cr-text-gray-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                {/* Tooltip */}
                <div className="cr-pointer-events-none cr-invisible cr-absolute cr-bottom-full cr-left-1/2 cr-z-10 cr-mb-2 cr-w-96 cr--translate-x-1/2 cr-transform cr-whitespace-normal cr-rounded-lg cr-bg-gray-900 cr-px-3 cr-py-2 cr-text-xs cr-text-white cr-opacity-0 cr-transition-all cr-duration-200 group-hover:cr-visible group-hover:cr-opacity-100">
                  When checked, this equipment will be private to your team and won&apos;t be visible to other teams.
                  <br />
                  Uncheck to make it available to all teams.
                  {isEditMode && (
                    <>
                      <br />
                      This setting can be changed by team members but may require admin approval for sharing.
                    </>
                  )}
                  <div className="cr-absolute cr-left-1/2 cr-top-full cr--translate-x-1/2 cr-transform cr-border-4 cr-border-transparent cr-border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Make Public checkbox for superusers */}
        {shouldShowMakePublicCheckbox() && (
          <div className="cr-mb-4">
            <div className="cr-flex cr-items-center cr-gap-2">
              <label className="cr-flex cr-items-center cr-gap-2">
                <input
                  type="checkbox"
                  checked={makePublic}
                  onChange={(e) => setMakePublic(e.target.checked)}
                  className="cr-focus:ring-red-500 cr-focus:ring-2 cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-bg-gray-100 cr-text-red-600"
                />
                <span className="cr-text-sm cr-font-medium cr-text-red-700">Make Public</span>
              </label>
              <div className="cr-group cr-relative">
                <svg
                  className="cr-h-4 cr-w-4 cr-cursor-help cr-text-gray-400 hover:cr-text-gray-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                {/* Tooltip */}
                <div className="cr-pointer-events-none cr-invisible cr-absolute cr-bottom-full cr-left-1/2 cr-z-10 cr-mb-2 cr-w-96 cr--translate-x-1/2 cr-transform cr-whitespace-normal cr-rounded-lg cr-bg-gray-900 cr-px-3 cr-py-2 cr-text-xs cr-text-white cr-opacity-0 cr-transition-all cr-duration-200 group-hover:cr-visible group-hover:cr-opacity-100">
                  <strong>Superuser only:</strong> This will permanently make the equipment public by removing the team
                  parameter. Once made public, it will be available to all teams and cannot be made private again
                  through the interface.
                  <div className="cr-absolute cr-left-1/2 cr-top-full cr--translate-x-1/2 cr-transform cr-border-4 cr-border-transparent cr-border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div
          className={`cr-flex cr-items-center cr-border-t cr-border-gray-200 cr-pt-4 ${isEditMode ? 'cr-justify-between' : 'cr-justify-end'}`}
        >
          {/* Delete button - only show in edit mode */}
          {isEditMode && (
            <button
              type="button"
              onClick={handleDelete}
              className="cr-disabled:cr-opacity-50 cr-rounded-md cr-bg-red-600 cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-white cr-transition-colors hover:cr-bg-red-700"
              disabled={isDeleting || isSubmitting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}

          <div className="cr-flex cr-gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-gray-700 cr-transition-colors hover:cr-bg-gray-50"
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cr-disabled:cr-opacity-50 cr-rounded-md cr-bg-blue-600 cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-white cr-transition-colors hover:cr-bg-blue-700"
              disabled={isSubmitting || isDeleting}
            >
              {isSubmitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Adding...'
                : isEditMode
                  ? 'Update Equipment'
                  : 'Add Equipment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EquipmentForm;
